import * as uaaRoutes from "../../../../modules/user-management/user-management-routing.module";
import * as warehouseManagementRoutes from "../../../../modules/warehouse-management/warehouse-management-routing.module";
import * as settingsRoutes from "../../../../modules/settings/settings-routing.module";
import { UserTypeEnum } from "../../../../store/entities/uaa/auth-user.model";
import { NgxPermissionsService } from "ngx-permissions";
import { Injectable } from "@angular/core";
import { StorageService } from "../../../../services/storage.service";

export interface MenuOption {
  name: string;
  key?: string;
  image?: string;
  shortName?: string;
  permission: string[];
  route: string[];
  pathRoute?: string[];
  queryParams?: any;
  description?: string;
  icon?: string;
  iconType?: 'SVG' | 'MAT';
  visible?: boolean;
  menuFor?: UserTypeEnum[];
  children?: MenuOption[];
}

function formatRoutesToMenu(routes: any[], modulePath: string = ''): MenuOption[] {
  const menuOptions: MenuOption[] = [];



  // Create a map of parentTitlePath to their corresponding children
  const parentTitlePathMap = new Map<string, MenuOption[]>();
  routes = routes?.filter(x => !!x?.data?.menu);
  for (const route of routes) {
    const { path, data } = route;

    if (data?.ignore !== true && path.length > 0) {
      const { parentTitlePath, menu } = data;

      if (parentTitlePath) {
        const parentChildren = parentTitlePathMap.get(parentTitlePath) || [];
        parentChildren.push({
          name: menu.title,
          route: !!modulePath ? [modulePath, path] : [path],
          iconType: menu?.iconType,
          icon: menu?.icon,
          menuFor: data?.menuFor,
          permission: data?.permissions?.only
        });
        parentTitlePathMap.set(parentTitlePath, parentChildren);
      }
    }
  }

  // Create menu items based on top-level routes (no parentTitlePath)
  for (const route of routes) {
    const { path, data } = route;
    if (data?.ignore !== true && path.length > 0) {
      const { parentTitlePath, menu } = data;
      if (!parentTitlePath) {
        const menuOption: MenuOption = {
          name: menu?.title,
          route: !!modulePath ? [modulePath, path] : [path],
          iconType: menu?.iconType,
          icon: menu?.icon,
          menuFor: data?.menuFor,
          permission: data?.permissions?.only
        };
        const children = parentTitlePathMap.get(path) || [];
        if (children.length > 0) {
          menuOption.children = children;
        }
        menuOptions.push(menuOption);
      }
    }
  }

  return menuOptions;
}

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  userDetails
  userAccountType
  userWarehouse
  constructor(private permissionService: NgxPermissionsService, private storageService: StorageService) {
    this.userDetails = storageService.get('userProfileDetails')
    this.userWarehouse = this.userDetails.warehouse_unique_id
    this.userAccountType = this.userDetails.account_type
    // console.log(this.userDetails, this.userAccountType, this.userWarehouse);

  }

  async hasPerm(permissionName: string): Promise<boolean> {
    return this.permissionService.hasPermission(permissionName);
  }


  // getMenuOptions is now an async function to await permission checks
  async getMenuOptions(): Promise<MenuOption[]> {
    const menuOptions: MenuOption[] = [
      {
        name: 'Temp & Humidity Monitoring',
        route: ['', 'manage', 'warehouse-management'],
        pathRoute: await this.hasPerm('can_view_dashboard') ? ['', 'manage', 'warehouse-management', 'dashboard'] : ['', 'manage', 'warehouse-management', 'warehouse-list'],
        queryParams: this.userAccountType === 'STAFF' || 'SUPERVISOR' ? { action: 'view', id: this.userWarehouse } : null,
        // queryParams: this.userAccountType === 'STAFF' ? null: null,
        description: 'View real-time temperature and humidity data across all warehouses and zones for environmental tracking.',
        icon: 'manufacturer_management',
        iconType: 'SVG',
        permission: ['NO_PERMISSION'],
        children: formatRoutesToMenu(warehouseManagementRoutes.routes[0].children, 'warehouse-management/'),
      },
      {
        name: 'User Management',
        route: ['', 'manage', 'user-management'],
        pathRoute: ['', 'manage', 'user-management', 'dashboard'],
        description: 'Manage user accounts, assign roles, and control access levels to ensure secure and organized platform usage',
        icon: 'user_management',
        iconType: 'SVG',
        permission: ['can_view_user', 'can_view_role'],
        children: formatRoutesToMenu(uaaRoutes.routes[0].children, 'user-management/'),
      },
      {
        name: 'Settings',
        route: ['', 'manage', 'settings'],
        pathRoute: ['', 'manage', 'settings', 'faqs'],
        description: 'Configure system preferences, manage alerts, and customize platform behavior to suit your operational needs',
        icon: 'settings',
        iconType: 'SVG',
        permission: ['can_configure_settings'],
        children: formatRoutesToMenu(settingsRoutes.routes[0].children, 'settings/'),
      },
    ];

    return menuOptions;
  }
}
